import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    contactAdminAdobe: string;
    shareContactOwner: string;
    overPlanLimitCanManage: string;
    overPlanLimitCannotManageFirstLine: string;
    contactOwnerGeneral: (ownerEmail: string) => string | React.ReactNode;
    overFreePlanLimitCanManage: string;
    paymentFailedOverLimitCanManage: string;
    contactOwnerPaymentProblem: (ownerEmail: string) => string | React.ReactNode;
    paymentFailedWithinLimitCanManage: string;
    contactSupport: string;
    buttonReviewUsage: string;
    buttonComparePlans: string;
    buttonUpdatePaymentDetails: string;
    buttonUpdatePaymentMethod: string;
    buttonBackToFrameio: string;
    buttonContactSupport: string;
};
export type Deps = {
    StyledLink: any;
};
export default {
    DEFINE(_messages: Text, { StyledLink }: Deps): void {
        return Object.defineProperties(this, {
            /** Contact message for Adobe-managed accounts */
            contactAdminAdobe: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "getAccountLockTypeVariant.contactAdminAdobe?c=1c4sxknq7xt21", defaultMessage: "Contact your admin for assistance." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Contact message for share access issues */
            shareContactOwner: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "getAccountLockTypeVariant.shareContactOwner?c=fvxkzgzzk8x4", defaultMessage: "Please contact the account owner for help with accessing this Share." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Message for accounts over plan limits beyond grace period (can manage billing) */
            overPlanLimitCanManage: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "getAccountLockTypeVariant.overPlanLimitCanManage?c=1dwyi2y0bautu", defaultMessage: "This account has exceeded plan limits beyond the grace period. Reduce usage or upgrade to unlock." }));
                },
                enumerable: true,
                configurable: true
            },
            /** First line for accounts over plan limits beyond grace period (cannot manage billing) */
            overPlanLimitCannotManageFirstLine: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "getAccountLockTypeVariant.overPlanLimitCannotManageFirstLine?c=29ro6os5d74co", defaultMessage: "This account has exceeded plan limits beyond the grace period." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Contact owner message for general assistance */
            contactOwnerGeneral: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (ownerEmail: string) => __intl__.intl().formatMessage({ id: "getAccountLockTypeVariant.contactOwnerGeneral?c=2cqg5aths5dk2", defaultMessage: "<jsx>Contact the account owner <StyledLink>({ownerEmail})</StyledLink> for assistance.</jsx>" }, { jsx: (__children__: React.ReactNode) => React.Children.toArray(__children__), StyledLink: (__children__: React.ReactNode) => <StyledLink href={`mailto:${ownerEmail}`} target="_blank">{React.Children.toArray(__children__)}</StyledLink>, ownerEmail: ownerEmail }));
                },
                enumerable: true,
                configurable: true
            },
            /** Message for free tier accounts over limits (can manage billing) */
            overFreePlanLimitCanManage: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "getAccountLockTypeVariant.overFreePlanLimitCanManage?c=t2m8zi4butln", defaultMessage: "This account has exceeded plan usage limits beyond the grace period. Reduce usage or upgrade to unlock." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Message for payment failure over plan limits (can manage billing) */
            paymentFailedOverLimitCanManage: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "getAccountLockTypeVariant.paymentFailedOverLimitCanManage?c=31gmi1no35gan", defaultMessage: "Our last payment attempts were unsuccessful. To unlock account, update payment details to resolve overdue balance." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Contact owner message for payment problem resolution */
            contactOwnerPaymentProblem: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (ownerEmail: string) => __intl__.intl().formatMessage({ id: "getAccountLockTypeVariant.contactOwnerPaymentProblem?c=3owe6xr4esd5h", defaultMessage: "<jsx>Contact the account owner <StyledLink>({ownerEmail})</StyledLink> for assistance resolving payment problem.</jsx>" }, { jsx: (__children__: React.ReactNode) => React.Children.toArray(__children__), StyledLink: (__children__: React.ReactNode) => <StyledLink href={`mailto:${ownerEmail}`} target="_blank">{React.Children.toArray(__children__)}</StyledLink>, ownerEmail: ownerEmail }));
                },
                enumerable: true,
                configurable: true
            },
            /** Message for payment failure within plan limits (can manage billing) */
            paymentFailedWithinLimitCanManage: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "getAccountLockTypeVariant.paymentFailedWithinLimitCanManage?c=ahtkqsx3gpin", defaultMessage: "Our last payment attempts were unsuccessful. Update payment method to try again." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Contact support message for manual locks */
            contactSupport: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "getAccountLockTypeVariant.contactSupport?c=3nzypmc977gqx", defaultMessage: "Contact Support for assistance." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Button text for reviewing usage */
            buttonReviewUsage: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "getAccountLockTypeVariant.buttonReviewUsage?c=37wpbnaa243fv", defaultMessage: "Review Usage" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Button text for comparing plans */
            buttonComparePlans: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "getAccountLockTypeVariant.buttonComparePlans?c=2iy5zyidalvzv", defaultMessage: "Compare Plans" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Button text for updating payment details */
            buttonUpdatePaymentDetails: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "getAccountLockTypeVariant.buttonUpdatePaymentDetails?c=335i812sik29x", defaultMessage: "Update Payment Details" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Button text for updating payment method */
            buttonUpdatePaymentMethod: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "getAccountLockTypeVariant.buttonUpdatePaymentMethod?c=38bhd717zb5cx", defaultMessage: "Update Payment Method" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Button text for returning to Frame.io */
            buttonBackToFrameio: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "getAccountLockTypeVariant.buttonBackToFrameio?c=dnme3gl1382", defaultMessage: "Back To Frame.io" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Button text for contacting support */
            buttonContactSupport: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "getAccountLockTypeVariant.buttonContactSupport?c=35oj9lyumr8a6", defaultMessage: "Contact Support" }));
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps: Deps): void;
};
